import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { ActionButton, GhostButton, PillBadge } from "../components/Primitives";
import UserMenu from "../components/UserMenu";
import { GeofenceMap } from "../components/GeofenceMap";
import { Clock, MapPin, ScrollText, LogOut, Loader2, AlertOctagon, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import TourStopsPanel from "../components/TourStopsPanel";
import StatusCheckModal from "../components/StatusCheckModal";
import { BreakBanner, StartBreakButton } from "../components/BreakBanner";
import ShiftPolicyCard from "../components/ShiftPolicyCard";

export default function GuardPortal() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [guard, setGuard] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [activeAttendance, setActiveAttendance] = useState(null);
  const [activeBreak, setActiveBreak] = useState(null);
  const [pendingPrompt, setPendingPrompt] = useState(null);
  const [siteCtx, setSiteCtx] = useState(null);
  const [loc, setLoc] = useState(null);
  const [busy, setBusy] = useState(null);
  const [postOrdersFor, setPostOrdersFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const [me, s, ctx] = await Promise.all([
        api.get("/portal/me"),
        api.get("/portal/shifts"),
        api.get("/portal/shift-context").catch(() => ({ data: null })),
      ]);
      setGuard(me.data); setShifts(s.data);
      if (ctx.data) {
        setActiveAttendance(ctx.data.attendance || null);
        setActiveBreak(ctx.data.active_break || null);
        setPendingPrompt(ctx.data.pending_prompt || null);
        setSiteCtx(ctx.data.site || null);
      }
    } catch (e) { toast.error(errorMessage(e, "Failed to load portal")); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Poll the shift-context every 30s so a newly-scheduled status-check prompt
  // shows up promptly without the guard refreshing the page.
  useEffect(() => {
    if (!activeAttendance) return undefined;
    const id = setInterval(async () => {
      try {
        const r = await api.get("/portal/shift-context");
        setActiveAttendance(r.data.attendance || null);
        setActiveBreak(r.data.active_break || null);
        setPendingPrompt(r.data.pending_prompt || null);
      } catch {/* silent — background poll */}
    }, 30000);
    return () => clearInterval(id);
  }, [activeAttendance]);

  useEffect(() => {
    if (!navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition(
      (pos) => setLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude, acc: pos.coords.accuracy }),
      () => {},
      { enableHighAccuracy: true, maximumAge: 30000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  const clockIn = async (shift) => {
    if (!loc) { toast.error("Waiting for GPS — allow location access."); return; }
    setBusy(shift.id);
    try {
      const r = await api.post("/portal/clock-in", { shift_id: shift.id, lat: loc.lat, lng: loc.lng });
      setActiveAttendance(r.data);
      toast.success("Clocked in. Stay safe out there.");
      load();
    } catch (e) { toast.error(errorMessage(e, "Clock-in failed")); }
    setBusy(null);
  };

  const clockOut = async () => {
    if (!activeAttendance) return;
    // If a break is active, refuse — the guard needs to end it first so the
    // timesheet gets the correct paid/unpaid classification.
    if (activeBreak) {
      toast.error("End your break before clocking out.");
      return;
    }
    // Snapshot the shift context before we clear it — we want to be able
    // to prompt "file DAR now?" and deep-link with the shift_id.
    const shiftIdForDar = activeAttendance.shift_id;
    const guardIdForDar = activeAttendance.guard_id;
    const shiftForDar = shifts.find((s) => s.id === shiftIdForDar);
    setBusy(activeAttendance.id);
    try {
      // Send current GPS so the server can verify the guard is still
      // inside the site's geofence. If GPS is unavailable, `loc` stays
      // null and the server preserves legacy "no coords, no check" —
      // never trap a guard on shift because of a dead sensor.
      const body = loc ? { lat: loc.lat, lng: loc.lng } : {};
      await api.post(`/portal/clock-out/${activeAttendance.id}`, body);
      setActiveAttendance(null);

      // Only surface the "File DAR" prompt when this guard hasn't already
      // filed a DAR for this shift. Prevents nagging on shifts where the
      // guard filed mid-shift, and matches the guard's expectation that
      // "clocked out + DAR done = nothing else to do".
      let hasDar = false;
      if (shiftIdForDar && guardIdForDar) {
        try {
          const existing = await api.get("/dar", {
            params: { shift_id: shiftIdForDar, guard_id: guardIdForDar },
          });
          hasDar = Array.isArray(existing.data) && existing.data.length > 0;
        } catch (err) {
          if (process.env.NODE_ENV !== "production") console.debug("DAR check skipped:", err?.response?.status);
        }
      }
      if (hasDar) {
        toast.success("Clocked out. Good job.", {
          description: "Your Daily Activity Report is already on file for this shift.",
          duration: 6000,
        });
      } else {
        // Toast with an inline "File DAR" action button so the guard has a
        // one-tap path from clock-out → filing the day's report.
        toast.success("Clocked out. Good job.", {
          description: shiftForDar
            ? `Wrap up ${shiftForDar.site_name || "your shift"} by filing the Daily Activity Report — takes 30 seconds with quick phrases.`
            : "File your DAR to keep report timeliness green.",
          action: {
            label: "File DAR",
            onClick: () => nav(`/dar?open=1&shift_id=${shiftIdForDar || ""}`),
          },
          duration: 8000,
        });
      }
      load();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "";
      // Backend rejects clock-out with 400 + "geofence" when the guard
      // has drifted outside the site's radius. Surface a dedicated
      // toast so the guard immediately knows to return before retrying.
      if (status === 400 && /geofence/i.test(detail)) {
        toast.error("You're outside the geofence", {
          description: `${detail}\n\nReturn to the site to clock out.`,
          duration: 10000,
        });
      } else {
        toast.error(errorMessage(e, "Clock-out failed"));
      }
    }
    setBusy(null);
  };

  const distM = (a, b) => {
    if (!a || !b || a.lat == null || b.lat == null) return null;
    const R = 6371000;
    const dLat = (b.lat - a.lat) * Math.PI / 180;
    const dLng = (b.lng - a.lng) * Math.PI / 180;
    const x = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(x));
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <UserMenu />
      <header className="border-b border-[#27272A] bg-[#121212] sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 label-mute">
              <ShieldCheck className="size-3 text-[#FF3B30]" /> Iris Security · Officer Portal
            </div>
            <div className="font-display text-xl uppercase font-bold tracking-tight mt-1">
              {guard?.name || user?.name || "Loading…"}
            </div>
            <div className="text-[10px] text-zinc-500 font-mono">
              {guard?.badge_no ? `Badge ${guard.badge_no} · ` : ""}{guard?.role || "guard"}
            </div>
          </div>
          {/* Sign out + change password moved to top-right UserMenu (rendered below) */}
        </div>
        <div className="max-w-3xl mx-auto px-5 pb-3 text-[10px] uppercase tracking-widest font-mono flex items-center gap-2">
          <MapPin className={`size-3 ${loc ? "text-[#34C759]" : "text-[#FF9F0A]"}`} />
          {loc ? (
            <span className="text-zinc-400">{loc.lat.toFixed(5)}, {loc.lng.toFixed(5)} · ±{Math.round(loc.acc)}m</span>
          ) : (
            <span className="text-[#FF9F0A]">awaiting GPS — allow location access</span>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-5 space-y-6">
        {/* Auto-appearing status-check acknowledgement prompt (server-scheduled) */}
        {pendingPrompt && (
          <StatusCheckModal
            prompt={pendingPrompt}
            onAcknowledged={() => { setPendingPrompt(null); load(); }}
          />
        )}

        {activeAttendance && (
          <div className="border border-[#34C759]/40 bg-[#34C759]/5 p-4" data-testid="active-clock-in">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="label-mute text-[#34C759] mb-1">On Shift</div>
                <div className="text-sm">Clocked in {new Date(activeAttendance.check_in_time).toLocaleTimeString()}</div>
                {siteCtx?.status_check_interval_minutes ? (
                  <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mt-1">
                    <ShieldCheck className="size-3 inline text-[#FF9F0A] mr-1" />
                    auto status-check every {siteCtx.status_check_interval_minutes}min
                  </div>
                ) : null}
              </div>
              <div className="flex flex-col sm:flex-row gap-2 items-end">
                {!activeBreak && siteCtx?.breaks_enabled !== false && (
                  <StartBreakButton onStarted={(b) => setActiveBreak(b)} />
                )}
                <ActionButton data-testid="clock-out-button" disabled={busy === activeAttendance.id || !!activeBreak} onClick={clockOut}>
                  {busy === activeAttendance.id ? <Loader2 className="size-4 animate-spin" /> : <LogOut className="size-4" />} Clock Out
                </ActionButton>
              </div>
            </div>
          </div>
        )}

        {/* Active break banner — auto-pauses status checks until "End Break" */}
        {activeBreak && (
          <BreakBanner
            activeBreak={activeBreak}
            paidMaxMinutes={siteCtx?.all_breaks_unpaid ? 0 : (siteCtx?.paid_break_max_minutes ?? 15)}
            onEnded={() => { setActiveBreak(null); load(); }}
          />
        )}

        {/* Pre-shift briefing — effective site policy for status checks + breaks */}
        {activeAttendance && siteCtx && (
          <ShiftPolicyCard site={siteCtx} />
        )}

        {activeAttendance?.shift_id && !activeBreak && (
          <TourStopsPanel shiftId={activeAttendance.shift_id} loc={loc} />
        )}

        <section>
          <div className="label-mute">My Shifts</div>
          <h2 className="font-display text-2xl uppercase font-bold tracking-tight mb-4">Upcoming & Recent</h2>
          {shifts.length === 0 ? (
            <div className="border border-[#27272A] bg-[#121212] p-8 text-center text-zinc-500 scanlines">No shifts assigned in the last 7 or next 30 days.</div>
          ) : (
            <div className="space-y-3" data-testid="my-shifts-list">
              {shifts.map((s) => {
                const start = new Date(s.start_time); const end = new Date(s.end_time);
                const now = new Date();
                const isLive = start <= now && end >= now;
                const d = distM(loc, { lat: s.site_lat, lng: s.site_lng });
                const radius = s.site_geofence_radius_m || 200;
                const inFence = d != null && d <= radius;
                return (
                  <div key={s.id} data-testid={`portal-shift-${s.id}`} className={`border p-4 ${isLive ? "border-[#34C759]/50 bg-[#34C759]/5" : "border-[#27272A] bg-[#121212]"}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="font-display text-lg uppercase font-bold tracking-tight">{s.site_name || "Unknown site"}</div>
                        <div className="text-xs text-zinc-400 mt-1">{s.site_address}</div>
                        <div className="font-mono text-xs text-zinc-500 mt-2">
                          {start.toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })} → {end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </div>
                        {d != null && (
                          <div className={`text-[10px] uppercase tracking-widest font-mono mt-2 ${inFence ? "text-[#34C759]" : "text-[#FF9F0A]"}`}>
                            {inFence ? `✓ within fence (${Math.round(d)}m / ${radius}m)` : `${Math.round(d)}m from site · ${radius}m fence`}
                          </div>
                        )}
                      </div>
                      <PillBadge tone={isLive ? "green" : s.status === "completed" ? "default" : "blue"}>
                        {isLive ? "now" : s.status}
                      </PillBadge>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 justify-end">
                      {(s.site_post_orders_text || s.site_post_orders_document) && (
                        <GhostButton data-testid={`portal-post-orders-${s.id}`} onClick={() => setPostOrdersFor(s)}>
                          <ScrollText className="size-3.5" /> Post Orders
                        </GhostButton>
                      )}
                      {!activeAttendance && isLive && (
                        <ActionButton data-testid={`portal-clock-in-${s.id}`} disabled={!inFence || busy === s.id} onClick={() => clockIn(s)}>
                          {busy === s.id ? <Loader2 className="size-4 animate-spin" /> : <Clock className="size-4" />}
                          {inFence ? "Clock In" : "Move closer"}
                        </ActionButton>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>

      {postOrdersFor && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setPostOrdersFor(null)}>
          <div className="w-full max-w-lg border border-[#27272A] bg-[#121212]" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-[#27272A] px-5 py-4 flex items-center justify-between">
              <div>
                <div className="label-mute flex items-center gap-1"><ScrollText className="size-3" /> Post Orders</div>
                <div className="font-display text-lg uppercase font-bold tracking-tight mt-1">{postOrdersFor.site_name}</div>
              </div>
              <button onClick={() => setPostOrdersFor(null)} className="text-zinc-400 hover:text-white">
                <AlertOctagon className="size-4" />
              </button>
            </div>
            {postOrdersFor.site_post_orders_updated_at && (
              <div className="px-5 pt-3 text-[10px] uppercase tracking-widest text-zinc-500">
                last updated {new Date(postOrdersFor.site_post_orders_updated_at).toLocaleString()}
              </div>
            )}
            <div className="p-5 text-sm whitespace-pre-wrap text-zinc-200" data-testid="portal-post-orders-text">
              {postOrdersFor.site_post_orders_text || <span className="text-zinc-500">No text instructions provided.</span>}
            </div>
            {postOrdersFor.site_post_orders_document && (
              <div className="px-5 pb-3">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${postOrdersFor.site_post_orders_document.storage_path}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="portal-post-orders-doc"
                  className="border border-[#27272A] p-3 flex items-center gap-2 hover:border-[#0A84FF] transition-colors"
                >
                  <ScrollText className="size-4 text-[#0A84FF]" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#0A84FF] truncate">{postOrdersFor.site_post_orders_document.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                      tap to open · {(postOrdersFor.site_post_orders_document.size / 1024).toFixed(1)} KB
                    </div>
                  </div>
                </a>
              </div>
            )}
            {postOrdersFor.site_lat != null && postOrdersFor.site_lng != null && (
              <div className="px-5 pb-5">
                <GeofenceMap lat={postOrdersFor.site_lat} lng={postOrdersFor.site_lng} radius_m={postOrdersFor.site_geofence_radius_m} height="200px" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
